/* =============================================================
   Cart Offers — JS (v8 — defensive re-render)
   Forces multiple updateRewards() calls after init to overcome
   any race condition where data attributes change after first read.
   ============================================================= */

(function () {
  'use strict';

  const SEL = {
    root: '[data-co-root]',
    fill: '[data-co-fill]',
    status: '[data-co-status]',
    statusText: '[data-co-status-text]',
    milestone: '[data-co-milestone]',
    bannerTrack: '[data-co-banner-track]',
  };

  function readConfig(root) {
    // Use getAttribute() directly — Minimog/web-components seem to interfere
    // with the standard dataset camelCase conversion, leaving keys like "tier-1"
    // instead of "tier1".
    const get = (name) => root.getAttribute(name) || '';

    if (window.__coDebug !== false) {
      console.log('[Cart Offers] Reading tier config via getAttribute:', {
        tier1: get('data-tier-1'),
        tier2: get('data-tier-2'),
        tier3: get('data-tier-3'),
        tier1Label: get('data-tier-1-label'),
        tier2Label: get('data-tier-2-label'),
        tier3Label: get('data-tier-3-label'),
      });
    }

    const t1 = parseInt(get('data-tier-1'), 10) || 490000;
    const t2 = parseInt(get('data-tier-2'), 10) || 749900;
    const t3 = parseInt(get('data-tier-3'), 10) || 1199900;

    return {
      tiers: [
        { paise: t1 > 0 ? t1 : 490000, label: get('data-tier-1-label') || 'Tier 1', amountText: get('data-tier-1-amount-text') },
        { paise: t2 > t1 ? t2 : t1 + 100000, label: get('data-tier-2-label') || 'Tier 2', amountText: get('data-tier-2-amount-text') },
        { paise: t3 > t2 ? t3 : t2 + 100000, label: get('data-tier-3-label') || 'Tier 3', amountText: get('data-tier-3-amount-text') },
      ],
      messages: {
        beforeTier1: get('data-status-pretier') || 'Add {amount} more to unlock {reward}',
        towardNext: get('data-status-toward') || 'Add {amount} more to unlock {reward}',
        allUnlocked: get('data-status-success') || 'Best reward unlocked',
      },
      currency: get('data-currency') || '₹',
      bannerRotationMs: parseInt(get('data-banner-rotation-ms'), 10) || 3000,
    };
  }

  function formatMoney(paise, currency) {
    const rupees = Math.round(paise / 100);
    return currency + rupees.toLocaleString('en-IN');
  }

  function interpolate(template, vars) {
    return template.replace(/\{(\w+)\}/g, (_, key) =>
      vars[key] !== undefined ? vars[key] : ''
    );
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async function addToCart(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  }

  function updateRewards(root, config, cartTotal) {
    const [t1, t2, t3] = config.tiers;

    if (window.__coDebug !== false) {
      console.log('[Cart Offers] updateRewards: cartTotal=', cartTotal,
                  'tiers (paise):', t1.paise, t2.paise, t3.paise);
    }

    let fillPercent = 0;
    if (cartTotal >= t3.paise) {
      fillPercent = 100;
    } else if (cartTotal >= t2.paise) {
      const range = t3.paise - t2.paise;
      fillPercent = 50 + ((cartTotal - t2.paise) / range) * 50;
    } else if (cartTotal >= t1.paise) {
      const range = t2.paise - t1.paise;
      fillPercent = ((cartTotal - t1.paise) / range) * 50;
    }

    const fill = root.querySelector(SEL.fill);
    if (fill) fill.style.width = fillPercent + '%';

    const milestones = root.querySelectorAll(SEL.milestone);
    milestones.forEach((m) => {
      const idx = parseInt(m.getAttribute('data-co-milestone'), 10);
      const tier = config.tiers[idx - 1];
      if (tier) m.classList.toggle('is-reached', cartTotal >= tier.paise);
    });

    const status = root.querySelector(SEL.status);
    const statusText = root.querySelector(SEL.statusText);
    if (!status || !statusText) return;

    if (cartTotal >= t3.paise) {
      status.classList.add('is-success');
      statusText.textContent = config.messages.allUnlocked;
    } else if (cartTotal >= t2.paise) {
      status.classList.remove('is-success');
      statusText.textContent = interpolate(config.messages.towardNext, {
        amount: formatMoney(t3.paise - cartTotal, config.currency),
        reward: t3.label,
      });
    } else if (cartTotal >= t1.paise) {
      status.classList.remove('is-success');
      statusText.textContent = interpolate(config.messages.towardNext, {
        amount: formatMoney(t2.paise - cartTotal, config.currency),
        reward: t2.label,
      });
    } else {
      status.classList.remove('is-success');
      statusText.textContent = interpolate(config.messages.beforeTier1, {
        amount: formatMoney(t1.paise - cartTotal, config.currency),
        reward: t1.label,
      });
    }
  }

  function initBannerCarousel(root, config) {
    const track = root.querySelector(SEL.bannerTrack);
    if (!track) return;
    const slides = Array.from(track.children);
    if (slides.length === 0) return;
    if (slides.length === 1) {
      slides[0].classList.add('is-active');
      return;
    }

    let idx = 0;
    slides[0].classList.add('is-active');

    setInterval(() => {
      slides[idx].classList.remove('is-active');
      idx = (idx + 1) % slides.length;
      slides[idx].classList.add('is-active');
    }, config.bannerRotationMs);
  }

  function wireUpsellButtons() {
    document.querySelectorAll('[data-add-variant]').forEach((btn) => {
      if (btn.getAttribute('data-co-wired')) return;
      btn.setAttribute('data-co-wired', '1');
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.getAttribute('data-add-variant');
        btn.disabled = true;
        try {
          await addToCart(variantId, 1);
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );
        } catch (err) {
          console.error('[Cart Offers] Add to cart failed:', err);
        } finally {
          btn.disabled = false;
        }
      });
    });
  }

  // -----------------------------
  // Refresh function — re-reads DOM each time, never caches config
  // -----------------------------
  async function refreshAll() {
    // ALWAYS re-query DOM and re-read config. Never cache between calls.
    const roots = document.querySelectorAll(SEL.root);
    if (!roots.length) {
      wireUpsellButtons();
      return;
    }

    try {
      const cart = await fetchCart();
      roots.forEach((root) => {
        const config = readConfig(root); // fresh read every time
       updateRewards(root, config, cart.original_total_price || cart.items_subtotal_price || cart.total_price);
      });
      wireUpsellButtons();
    } catch (err) {
      console.error('[Cart Offers] Refresh failed:', err);
    }
  }

  // -----------------------------
  // Init — runs MULTIPLE times to overcome race conditions
  // -----------------------------
  function init() {
    const roots = document.querySelectorAll(SEL.root);
    if (!roots.length) return;

    roots.forEach((root) => {
      const config = readConfig(root);
      initBannerCarousel(root, config);
    });

    // Run refresh IMMEDIATELY
    refreshAll();

    // Run AGAIN after 100ms (catches case where DOM was updated post-init)
    setTimeout(refreshAll, 100);

    // Run AGAIN after 500ms (catches Minimog's late initialization)
    setTimeout(refreshAll, 500);

    // Run AGAIN after 1500ms (final safety net)
    setTimeout(refreshAll, 1500);

    // Listen for cart events
    const events = [
      'cart:refresh', 'cart:updated', 'cart:change', 'cart:added', 'cart:build',
      'ajaxProduct:added', 'minimog:cart:updated', 'shopify:section:load',
    ];
    events.forEach((ev) => {
      document.addEventListener(ev, refreshAll);
      document.documentElement.addEventListener(ev, refreshAll);
      window.addEventListener(ev, refreshAll);
    });

    // Minimog PUB_SUB_EVENTS
    function subscribeMinimog() {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        Object.values(window.PUB_SUB_EVENTS).forEach((eventName) => {
          if (typeof eventName === 'string' && eventName.includes('cart')) {
            try {
              window.subscribe(eventName, () => setTimeout(refreshAll, 50));
            } catch (e) { /* ignore */ }
          }
        });
        return true;
      }
      return false;
    }

    if (!subscribeMinimog()) {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (subscribeMinimog() || attempts > 20) clearInterval(interval);
      }, 250);
    }

    // MutationObserver: trigger refresh whenever cart drawer DOM changes
    // This catches the case where Minimog re-renders or replaces parts of the drawer.
    const cartBody = document.getElementById('MinimogCartDrawerBody');
    if (cartBody) {
      const observer = new MutationObserver(() => {
        clearTimeout(window.__coRefreshTimer);
        window.__coRefreshTimer = setTimeout(refreshAll, 100);
      });
      observer.observe(cartBody, { childList: true, subtree: true });
    }

    // Watch the bar itself for attribute changes — if Minimog updates data-tier-* on the fly
    roots.forEach((root) => {
      const attrObserver = new MutationObserver(() => {
        if (window.__coDebug !== false) {
          console.log('[Cart Offers] Data attributes changed, re-running refreshAll');
        }
        clearTimeout(window.__coAttrTimer);
        window.__coAttrTimer = setTimeout(refreshAll, 50);
      });
      attrObserver.observe(root, { attributes: true });
    });

    // Fetch interceptor
    const origFetch = window.fetch;
    window.fetch = async function () {
      const response = await origFetch.apply(this, arguments);
      try {
        const url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
        if (/\/cart\/(add|change|update|clear)/.test(url) && response.ok) {
          setTimeout(refreshAll, 100);
        }
      } catch (e) { /* ignore */ }
      return response;
    };
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector(SEL.root)) {
      init();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for manual debugging from console
  window.__coRefresh = refreshAll;
  window.__coDebug = true;
})();